import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserCog,
  Plus,
  Search,
  Trash2,
  RefreshCw,
  AlertCircle,
  Building2,
  Percent,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, Modal, Select } from '../../components/ui';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

interface Manager {
  id: string;
  userId: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  tenantId: string;
  tenant: {
    id: string;
    name: string;
  };
  assignedAt: string;
  commissionConfig?: {
    percentageRate: number;
    fixedAmount: number;
  };
}

interface User {
  id: string;
  name: string;
  email: string;
}

interface Tenant {
  id: string;
  name: string;
}

export function AdminManagers() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState<Manager | null>(null);
  const [formData, setFormData] = useState({
    userId: '',
    tenantId: '',
  });
  const [commissionData, setCommissionData] = useState({
    percentageRate: 0,
    fixedAmount: 0,
  });

  // Buscar gerentes (via roles)
  const { data: managersData, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-managers'],
    queryFn: async () => {
      // Get all tenants first, then get managers for each
      const tenantsResponse = await api.get(ENDPOINTS.tenants.list);
      const tenants = tenantsResponse.data.tenants || tenantsResponse.data || [];
      
      const allManagers: Manager[] = [];
      for (const tenant of tenants) {
        try {
          const managersResponse = await api.get(ENDPOINTS.roles.tenantManagers(tenant.id));
          const managers = managersResponse.data || [];
          allManagers.push(...managers.map((m: any) => ({
            ...m,
            tenant: { id: tenant.id, name: tenant.name }
          })));
        } catch {
          // Tenant may not have managers
        }
      }
      return { managers: allManagers };
    },
  });

  // Buscar usuários disponíveis
  const { data: usersData } = useQuery({
    queryKey: ['users-list'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.users.list);
      return response.data as { users: User[] };
    },
  });

  // Buscar tenants
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants-list'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.tenants.list);
      return response.data as { tenants: Tenant[] };
    },
  });

  // Atribuir gerente
  const assignMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await api.post(ENDPOINTS.roles.assign, {
        userId: data.userId,
        role: 'GERENTE',
        tenantId: data.tenantId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-managers'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  // Revogar gerente
  const revokeMutation = useMutation({
    mutationFn: async (roleAssignmentId: string) => {
      const response = await api.delete(ENDPOINTS.roles.revoke(roleAssignmentId));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-managers'] });
    },
  });

  // Configurar comissão
  const commissionMutation = useMutation({
    mutationFn: async ({ managerId, data }: { managerId: string; data: typeof commissionData }) => {
      const response = await api.post(ENDPOINTS.managerCommissions.setConfig, {
        managerId,
        percentageRate: data.percentageRate,
        fixedAmount: data.fixedAmount * 100, // converter para centavos
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-managers'] });
      setIsCommissionModalOpen(false);
      setSelectedManager(null);
    },
  });

  const resetForm = () => {
    setFormData({ userId: '', tenantId: '' });
  };

  const handleConfigureCommission = (manager: Manager) => {
    setSelectedManager(manager);
    setCommissionData({
      percentageRate: manager.commissionConfig?.percentageRate || 0,
      fixedAmount: (manager.commissionConfig?.fixedAmount || 0) / 100,
    });
    setIsCommissionModalOpen(true);
  };

  const handleRevoke = (roleAssignmentId: string) => {
    if (confirm('Tem certeza que deseja remover este gerente? Ele perderá acesso ao tenant.')) {
      revokeMutation.mutate(roleAssignmentId);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    assignMutation.mutate(formData);
  };

  const handleCommissionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedManager) {
      commissionMutation.mutate({ managerId: selectedManager.userId, data: commissionData });
    }
  };

  const managers = managersData?.managers || [];
  const users = usersData?.users || [];
  const tenants = tenantsData?.tenants || [];

  const filteredManagers = managers.filter(manager =>
    manager.user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    manager.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    manager.tenant.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(cents / 100);
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-surface-100">Erro ao carregar gerentes</h2>
        <Button variant="primary" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Gestão de Gerentes</h1>
          <p className="text-surface-400">Atribua gerentes a tenants e configure comissões</p>
        </div>
        <Button variant="primary" onClick={() => { resetForm(); setIsModalOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Gerente
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nome, email ou tenant..."
                leftIcon={<Search className="w-4 h-4" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Managers List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCog className="w-5 h-5 text-primary-400" />
            Gerentes Atribuídos
            <Badge variant="info">{filteredManagers.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-20" />
              ))}
            </div>
          ) : filteredManagers.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <UserCog className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum gerente atribuído</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-800">
              {filteredManagers.map(manager => (
                <div
                  key={manager.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
                      <UserCog className="w-6 h-6 text-primary-400" />
                    </div>
                    <div>
                      <p className="font-medium text-surface-100">{manager.user.name}</p>
                      <p className="text-sm text-surface-500">{manager.user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-surface-500" />
                      <span className="text-surface-300">{manager.tenant.name}</span>
                    </div>
                    {manager.commissionConfig && (
                      <div className="text-right">
                        <p className="text-sm text-surface-400">Comissão</p>
                        <p className="font-medium text-surface-100">
                          {manager.commissionConfig.percentageRate}%
                          {manager.commissionConfig.fixedAmount > 0 && ` + ${formatCurrency(manager.commissionConfig.fixedAmount)}`}
                        </p>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleConfigureCommission(manager)} title="Configurar Comissão">
                        <Percent className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleRevoke(manager.id)} title="Remover">
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Assign Manager Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); resetForm(); }}
        title="Atribuir Gerente"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Usuário"
            value={formData.userId}
            onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
            options={[
              { value: '', label: 'Selecione um usuário' },
              ...users.map(u => ({ value: u.id, label: `${u.name} (${u.email})` })),
            ]}
            required
          />
          <Select
            label="Tenant"
            value={formData.tenantId}
            onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
            options={[
              { value: '', label: 'Selecione um tenant' },
              ...tenants.map(t => ({ value: t.id, label: t.name })),
            ]}
            required
          />
          <div className="p-3 bg-surface-800 rounded-lg text-sm text-surface-400">
            <p className="font-medium text-surface-300 mb-1">Permissões do Gerente:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Gerenciar usuários do tenant</li>
              <li>Definir limites de operação</li>
              <li>Aprovar/rejeitar solicitações</li>
              <li>Visualizar relatórios do tenant</li>
            </ul>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={assignMutation.isPending}>
              Atribuir
            </Button>
          </div>
        </form>
      </Modal>

      {/* Configure Commission Modal */}
      <Modal
        isOpen={isCommissionModalOpen}
        onClose={() => { setIsCommissionModalOpen(false); setSelectedManager(null); }}
        title="Configurar Comissão do Gerente"
      >
        <form onSubmit={handleCommissionSubmit} className="space-y-4">
          {selectedManager && (
            <div className="p-3 bg-surface-800 rounded-lg">
              <p className="text-sm text-surface-400">Gerente</p>
              <p className="font-medium text-surface-100">{selectedManager.user.name}</p>
              <p className="text-sm text-surface-500">{selectedManager.tenant.name}</p>
            </div>
          )}
          <Input
            label="Percentual de Comissão (%)"
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={commissionData.percentageRate}
            onChange={(e) => setCommissionData({ ...commissionData, percentageRate: parseFloat(e.target.value) || 0 })}
          />
          <Input
            label="Valor Fixo por Operação (R$)"
            type="number"
            step="0.01"
            min="0"
            value={commissionData.fixedAmount}
            onChange={(e) => setCommissionData({ ...commissionData, fixedAmount: parseFloat(e.target.value) || 0 })}
          />
          <div className="p-3 bg-primary-500/10 border border-primary-500/30 rounded-lg text-sm">
            <p className="text-surface-300">
              A comissão será calculada sobre cada operação aprovada pelo gerente.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsCommissionModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={commissionMutation.isPending}>
              Salvar
            </Button>
          </div>
        </form>
      </Modal>
    </motion.div>
  );
}
